import { Box, Button, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import SingleDatePicker from '../../../components/form/SingleDatePicker';
import React, { useEffect, useMemo, useState } from 'react';
import ProfileCard from '../../common/ProfileCard';
import ItemDetailsTable from '../../common/ItemDetailsTable';
import pricingEngineForDocuments, {
  buildSubmitLineItemPrice,
  normalizeLineItemPrice,
} from '../../../utils/pricingEngineForDocuments';
import AddLineItemButton from '../../../components/form/AddLineItemButton';
import BusinessProfileSelect from '../../../components/form/BusinessProfileSelect';
import CustomerSelect from '../../../components/form/CustomerSelect';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import TopBar from '../../../components/TopBar';
import PaymentMeansFields from '../../../components/form/PaymentMeansFields';
import { applyApiErrors } from '../../../utils/apiFieldErrors';
import DocumentCurrencyInput from '../../../components/form/DocumentCurrencyInput';
import { getInvoiceValidationSchema } from '../../../validations/InvoiceValidation';
import CustomTextInput from '../../../components/CustomTextInput';
import DocumentSummarySection from '../../../components/DocumentSummarySection';
import { Print as PrintIcon } from '@mui/icons-material';
import { usePdfActions } from '../../common/DocumentPdf';
import CopyTextComponent from '../../../components/CopyTextComponent';
import { useSelector } from 'react-redux';
import DocumentLevelChargeAndAllowancesButton from '../../common/DocumentLevelChargeAndAllowancesButton';
import {
  DEFAULT_CURRENCY_CODE,
  DEFAULT_DOCUMENT_SOURCE,
  DEFAULT_TRANSITION_TYPE_CODE,
  INVOICE_DOCUMENT_TYPE_CODE,
  UAE_COUNTRY_CODE,
} from '../../../constants/defaults';
import TransactionTypeFields from '../../../components/form/TransactionTypeFields';
import { transactionTypePositionCheck } from '../../../utils/helper';
import { buildInvoicePeriodPayload } from '../../../utils/invoicePeriodPayload';
import { getCountryOptionByCode, getSubdivisionOptionByCode } from '../../../api/codelist/codeListsSlice';
import { useAutoPopulateForm } from '../../../hooks/useAutoPopulateForm';
import { useNavigate } from 'react-router-dom';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { nowInGulfTimeString } from '../../../utils/formatDate';
import { buildPaymentMeansPayload } from '../../../utils/paymentMeansPayload';
import DocumentNoteField from '../../common/DocumentNoteField';

export default function ProformaInvoiceForm({
  initialData = {},
  onSubmit,
  onCancel,
  readOnly = false,
  isEdit = false,
  apiErrors = {},
  isLoading = false,
}) {
  const { handlePrintPdf } = usePdfActions();
  const { proformaInvoices: pi, validations, language, eInvoiceForm } = useTranslatedMessages();
  const pif = pi?.form ?? {};
  const documentNoteMessages = eInvoiceForm.document_note;
  const validationSchema = useMemo(() => getInvoiceValidationSchema(validations), [validations]);
  const subscription = useSelector((state) => state.subscription.subscription);
  const { settings } = useSelector((state) => state.org.org);
  const [__, setChargeEditing] = useState(false);
  const [_, setAllowanceEditing] = useState(false);
  const [profilesData, setProfilesData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const navigate = useNavigate();

  const backAction = () => {
    if (readOnly) {
      navigate(-1);
    } else {
      onCancel();
    }
  };

  const countryOption = useSelector(
    getCountryOptionByCode(initialData.delivery?.delivery_location?.address?.country_code),
  );

  const subdivisionOption = useSelector(
    getSubdivisionOptionByCode(initialData.delivery?.delivery_location?.address?.country_subentity),
  );

  const paymentMeansData = Array.isArray(initialData?.payment_means)
    ? initialData.payment_means
    : initialData?.payment_means
      ? [initialData.payment_means]
      : [];

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    context: {
      auto_numbering_enabled: settings?.auto_numbering_enabled ?? false,
    },
    mode: 'onChange',
    defaultValues: {
      invoice_type_code: INVOICE_DOCUMENT_TYPE_CODE,
      document_source: DEFAULT_DOCUMENT_SOURCE,

      profile_execution_id: initialData?.profile_execution_id || DEFAULT_TRANSITION_TYPE_CODE,

      document_number: initialData?.document_number || '',
      note: initialData?.note || '',
      issue_date: dayjs(initialData?.issue_date) || null,
      supply_date: initialData?.delivery?.actual_delivery_date
        ? dayjs(initialData.delivery.actual_delivery_date)
        : null,
      due_date: initialData?.due_date ? dayjs(initialData.due_date) : null,
      issue_time: nowInGulfTimeString(),

      payment_means: paymentMeansData,
      document_lines: (initialData?.document_lines ?? []).map((item, index) => ({
        ...item,
        key: item?.key || `ITM-${index}`,
        ...(item.price && { price: normalizeLineItemPrice(item.price) }),
      })),

      document_currency_code: initialData?.document_currency_code || 'AED',
      tax_exchange_rate: initialData?.tax_exchange_rate || null,
      accounting_supplier_party: initialData?.accounting_supplier_party || null,
      accounting_customer_party: initialData?.accounting_customer_party || null,
      charges: initialData?.charges || [],
      allowances: initialData?.allowances || [],

      invoice_period_start_date: initialData?.invoice_period?.start_date
        ? dayjs(initialData.invoice_period.start_date)
        : null,
      invoice_period_end_date: initialData?.invoice_period?.end_date
        ? dayjs(initialData.invoice_period.end_date)
        : null,
      invoice_period_description: initialData?.invoice_period?.description || null,

      principle_id_trn: initialData?.seller_supplier_party?.id || null,
      beneficiary_id: initialData?.buyer_customer_party?.id || null,
      delivery_party_name: initialData?.delivery?.party_name || null,
      incoterms: initialData?.delivery?.terms || null,

      delivery_street_name: initialData?.delivery?.delivery_location?.address?.street_name || null,
      delivery_city_name: initialData?.delivery?.delivery_location?.address?.city_name || null,
      delivery_country_code: initialData?.delivery?.delivery_location?.address?.country_code ? countryOption : null,
      delivery_country_subentity:
        countryOption?.code === UAE_COUNTRY_CODE
          ? subdivisionOption
          : initialData.delivery?.delivery_location?.address?.country_subentity,

      delivery_postal_zone: initialData?.delivery?.delivery_location?.address?.postal_zone || null,
    },
  });

  useEffect(() => {
    applyApiErrors(setError, apiErrors);
  }, [setError, apiErrors]);

  const {
    fields: lineItems,
    replace: replaceLineItems,
    append: appendLineItem,
    remove: removeLineItem,
  } = useFieldArray({
    control,
    name: 'document_lines',
  });

  const { remove: chargeRemove } = useFieldArray({
    control: control,
    name: 'charges',
  });

  const { remove: allowanceRemove } = useFieldArray({
    control: control,
    name: 'allowances',
  });

  const handleChargeEdit = () => {
    setChargeEditing(true);
  };

  const handleDiscountEdit = () => {
    setAllowanceEditing(true);
  };
  const handleSetBilledTo = (data) => {
    setValue('accounting_supplier_party', data);
  };

  useAutoPopulateForm({
    profiles: profilesData,
    customers: customerData,
    suppliers: supplierData,
    documentType: 'SALES',
    setValue,
    getValues,
  });

  const billedBy = useWatch({ control, name: 'accounting_supplier_party' });
  const billedTo = useWatch({ control, name: 'accounting_customer_party' });
  const document_lines = useWatch({ control, name: 'document_lines' }) || [];
  const docCharges = useWatch({ control, name: 'charges' }) || [];
  const docAllowances = useWatch({ control, name: 'allowances' }) || [];
  const profileExecutionId = useWatch({ control, name: 'profile_execution_id' });
  const watchedPaymentMeans = useWatch({
    control,
    name: 'payment_means',
  });
  const conversionRate = useWatch({ control, name: 'tax_exchange_rate.calculation_rate' });

  const documentDate = useWatch({ control, name: 'issue_date' });

  useEffect(() => {
    trigger(['document_lines', 'allowances', 'charges']);
  }, [profileExecutionId, trigger]);

  useEffect(() => {
    if (Object.keys(errors || {}).length) {
      trigger();
    }
    trigger('due_date');
  }, [language, documentDate, trigger]);

  const handleFormSubmit = async (data, isDraft) => {
    // Transform document_lines to ensure proper price structure
    const transformedDocumentLines = data.document_lines?.map((line) => ({
      ...line,
      price: buildSubmitLineItemPrice(line.price),
    }));

    const shouldSendDeliveryData =
      transactionTypePositionCheck(data.profile_execution_id, 7) ||
      transactionTypePositionCheck(data.profile_execution_id, 8);
    const shouldSendInvoicePeriodData = transactionTypePositionCheck(data.profile_execution_id, 4);
    const shouldSendPrincipleIdTrnData = transactionTypePositionCheck(data.profile_execution_id, 6);
    const shouldSendBeneficiaryId = transactionTypePositionCheck(data.profile_execution_id, 1);
    const paymentMeans = buildPaymentMeansPayload(data.payment_means, data.profile_execution_id);
    const actualDeliveryDate =
      data.supply_date && dayjs(data.supply_date).isValid() ? dayjs(data.supply_date).format('YYYY-MM-DD') : null;
    const { supply_date: _supplyDate, ...formData } = data;

    const payload = {
      ...formData,
      document_lines: transformedDocumentLines,
      due_date: data.due_date ? dayjs(data.due_date).format('YYYY-MM-DD') : null,
      issue_date: dayjs(data.issue_date).format('YYYY-MM-DD'),
      issue_time: nowInGulfTimeString(),
      sub_total: subtotal,
      total_excluding_tax: totalExcludingTax,
      total_tax: totalTaxAmount,
      total_including_tax: totalIncludingTax,
      total_payable: totalPayableAmount,
      header_allowance: headerAllowance,
      header_charges: headerCharges,

      ...(paymentMeans !== undefined && {
        payment_means: paymentMeans,
      }),

      total_paid: 0,
      ...(data?.document_currency_code !== DEFAULT_CURRENCY_CODE && {
        tax_currency_code: DEFAULT_CURRENCY_CODE,
      }),

      ...(shouldSendPrincipleIdTrnData && {
        seller_supplier_party: {
          id: data?.principle_id_trn,
        },
      }),
      ...(shouldSendBeneficiaryId && {
        buyer_customer_party: {
          id: data?.beneficiary_id,
        },
      }),
      ...(shouldSendInvoicePeriodData && {
        invoice_period: buildInvoicePeriodPayload(data),
      }),
      ...(shouldSendDeliveryData && {
        delivery: {
          ...(actualDeliveryDate && { actual_delivery_date: actualDeliveryDate }),
          party_name: data?.delivery_party_name,
          terms: data?.incoterms,
          delivery_location: {
            address: {
              street_name: data?.delivery_street_name,
              city_name: data?.delivery_city_name,
              country_code: data?.delivery_country_code?.code,
              country: data?.delivery_country_code?.label,
              country_subentity:
                data?.delivery_country_code?.code === UAE_COUNTRY_CODE
                  ? data?.delivery_country_subentity?.code
                  : data?.delivery_country_subentity,
              postal_zone: data?.delivery_postal_zone,
            },
          },
        },
      }),
      ...(!shouldSendDeliveryData &&
        actualDeliveryDate && {
          delivery: {
            actual_delivery_date: actualDeliveryDate,
          },
        }),
    };
    onSubmit(payload, isDraft);
  };

  const {
    itemsSubtotal,
    subtotal,
    totalTaxAmount,
    taxInAED,
    totalItemAllowances,
    totalExcludingTax,
    totalIncludingTax,
    totalItemCharges,
    totalPayableAmount,
    totalTaxableAmount,
    totalNonTaxableAmount,
    taxBreakdown,
    headerAllowance,
    headerCharges,
    payableRoundingAmount,
  } = pricingEngineForDocuments({
    items: document_lines,
    docCharges: docCharges,
    docAllowances: docAllowances,
    conversionRatePerAED: conversionRate,
    ...(readOnly &&
      initialData?.payable_rounding_amount != null && {
        payableRoundingAmount: initialData.payable_rounding_amount,
      }),
  });

  useEffect(() => {
    setValue('_total_payable', totalPayableAmount, { shouldValidate: false });
    trigger('due_date');
  }, [totalPayableAmount, profileExecutionId, setValue, trigger]);

  let printDisabled = initialData.status === 'DRAFT';

  return (
    <>
      <Box className="InvoiceDetails" sx={{ pt: 2, pb: 4 }}>
        <Grid
          container
          sx={{
            display: 'block',
          }}
        >
          <Grid>
            <TopBar
              title={
                <Stack direction="row" gap={3}>
                  {readOnly ? (
                    <>
                      {'Proforma Invoice Details |'} <CopyTextComponent copyText={initialData.id} />{' '}
                    </>
                  ) : (
                    pif?.title
                  )}
                </Stack>
              }
            >
              <Stack direction="row" spacing={1}>
                {readOnly && (
                  <>
                    <Button
                      data-testid="print-button"
                      variant="outlined"
                      color="primary"
                      disabled={printDisabled}
                      onClick={() =>
                        handlePrintPdf({
                          url: `proforma-invoices/${initialData.id}/download-pdf`,
                          fileName: `PINV-${initialData.id}.pdf`,
                        })
                      }
                    >
                      <PrintIcon fontSize="small" sx={{ mr: 1, color: printDisabled ? 'grey.500' : 'primary.main' }} />
                      Print
                    </Button>
                  </>
                )}
                <Button
                  data-testid="cancel-button"
                  variant={readOnly ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={backAction}
                  disabled={isLoading}
                >
                  {readOnly ? pif?.actions?.back : pif?.actions?.cancel}
                </Button>

                {!readOnly && (
                  <>
                    <Button
                      data-testid="draft-submit-button"
                      variant="outlined"
                      color="primary"
                      onClick={handleSubmit((data) => handleFormSubmit(data, true))}
                      disabled={isLoading}
                    >
                      {pif?.actions?.save_as_draft ?? 'Save as draft'}
                    </Button>
                    <Button
                      data-testid="submit-button"
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit((data) => handleFormSubmit(data, false))}
                      disabled={isLoading}
                    >
                      {pif?.actions?.create ?? 'Create'}
                    </Button>
                  </>
                )}
              </Stack>
            </TopBar>
          </Grid>
        </Grid>
      </Box>

      <Box className="InvoiceDetails">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextInput
              data-testid="document-number-input"
              label={pif?.fields?.invoice_number ?? 'Invoice Number'}
              control={control}
              errors={errors}
              name={'document_number'}
              readOnly={readOnly || isEdit}
              disabled={settings?.auto_numbering_enabled || isEdit}
              helperText={
                settings?.auto_numbering_enabled
                  ? (pif?.fields?.auto_numbering_helper ?? 'Document number will be generated automatically')
                  : ''
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SingleDatePicker
              data-testid="invoice-date-input"
              name="issue_date"
              label={pif?.fields?.invoice_issue_date ?? 'Invoice Issue Date'}
              control={control}
              errors={errors}
              readOnly={readOnly}
              maxDate={dayjs()}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SingleDatePicker
              data-testid="invoice-delivery-date-input"
              name="supply_date"
              label={pif?.fields?.supply_date ?? 'Delivery Date'}
              control={control}
              errors={errors}
              readOnly={readOnly}
              clearable
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SingleDatePicker
              data-testid="invoice-due-date-input"
              name="due_date"
              label={pif?.fields?.due_date ?? 'Due Date'}
              control={control}
              errors={errors}
              readOnly={readOnly}
              clearable
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <PaymentMeansFields
            control={control}
            errors={errors}
            readOnly={readOnly}
            watchedPaymentMeans={watchedPaymentMeans}
            setValue={setValue}
          />
          <TransactionTypeFields
            control={control}
            errors={errors}
            readOnly={readOnly}
            profileExecutionId={profileExecutionId}
            setValue={setValue}
            trigger={trigger}
          />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 3 }} alignItems="stretch">
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            <BusinessProfileSelect
              data-testid="billed-by-select"
              label={pif?.billed_by?.label ?? 'Billed by'}
              name="accounting_supplier_party"
              control={control}
              errors={errors}
              readOnly={readOnly}
              onProfilesLoaded={setProfilesData}
            />
            {billedBy && (
              <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ProfileCard
                  title={pif?.billed_by?.title ?? 'Billed By'}
                  subtitle={pif?.billed_by?.subtitle ?? 'Seller Info'}
                  document={{
                    ...billedBy,
                  }}
                />
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            <CustomerSelect
              data-testid="billed-to-select"
              label={pif?.billed_to?.label ?? 'Billed to'}
              name="accounting_customer_party"
              control={control}
              errors={errors}
              readOnly={readOnly}
              onSelect={handleSetBilledTo}
              setError={setError}
              onCustomerLoaded={setCustomerData}
            />
            {billedTo && (
              <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ProfileCard
                  title={pif?.billed_to?.title ?? 'Billed To'}
                  subtitle={pif?.billed_to?.subtitle ?? 'Buyer Info'}
                  rawData={billedTo}
                  readOnly={readOnly}
                  onUpdate={handleSetBilledTo}
                  document={{
                    ...billedTo, // Use the selected value from the form state
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Grid>
                {!readOnly && (
                  <AddLineItemButton
                    errors={errors}
                    addItem={appendLineItem}
                    lineItems={lineItems}
                    invoiceTypeCode={INVOICE_DOCUMENT_TYPE_CODE}
                    profileExecutionId={profileExecutionId}
                  />
                )}
              </Grid>
              <Grid container spacing={2} direction="row" alignItems="center" marginBottom={'1.2em'}>
                <DocumentCurrencyInput
                  control={control}
                  errors={errors}
                  documentCurrencyCode={useWatch({
                    control,
                    name: 'document_currency_code',
                  })}
                  readOnly={readOnly}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
            <ItemDetailsTable
              items={lineItems}
              remove={removeLineItem}
              replace={replaceLineItems}
              add={appendLineItem}
              readOnly={readOnly}
              invoiceTypeCode={INVOICE_DOCUMENT_TYPE_CODE}
              profileExecutionId={profileExecutionId}
              errors={errors}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ md: 6 }}>
            <DocumentLevelChargeAndAllowancesButton
              control={control}
              readOnly={readOnly}
              currentNetAmount={totalExcludingTax}
              itemBaseAmount={totalExcludingTax}
              invoiceTypeCode={INVOICE_DOCUMENT_TYPE_CODE}
              profileExecutionId={profileExecutionId}
              errors={errors}
            />
          </Grid>
          <Grid size={{ md: 6 }}>
            <DocumentSummarySection
              itemsSubtotal={itemsSubtotal}
              subtotal={subtotal}
              totalItemAllowances={totalItemAllowances}
              totalItemCharges={totalItemCharges}
              totalExcludingTax={totalExcludingTax}
              totalIncludingTax={totalIncludingTax}
              totalTaxAmount={totalTaxAmount}
              taxInAED={taxInAED}
              totalPayableAmount={totalPayableAmount}
              documentCurrencyCode={getValues('document_currency_code')}
              headerCharges={headerCharges}
              headerAllowance={headerAllowance}
              totalTaxableAmount={totalTaxableAmount}
              totalNonTaxableAmount={totalNonTaxableAmount}
              taxBreakdown={taxBreakdown}
              payableRoundingAmount={payableRoundingAmount}
            />
          </Grid>
        </Grid>
        <DocumentNoteField
          control={control}
          errors={errors}
          readOnly={readOnly}
          value={initialData?.note}
          messages={documentNoteMessages}
        />
      </Box>
    </>
  );
}
